/**
 * Lightweight Markdown renderer (no external deps).
 * Supports:
 *   - Headings: # H1, ## H2, ### H3
 *   - Bold: **text** or __text__
 *   - Italic: *text* or _text_
 *   - Inline code: `code`
 *   - Links: [label](https://...)
 *   - Unordered lists: lines starting with "- " or "* "
 *   - Ordered lists: lines starting with "1. ", "2. " ...
 *   - Blockquote: lines starting with "> "
 *   - Paragraphs separated by blank lines.
 *
 * Safe: never injects raw HTML — every node is rendered via React elements.
 */
import { Fragment, type ReactNode } from "react";

function renderInline(text: string, keyPrefix = ""): ReactNode[] {
  // Order matters: code → link → bold → italic.
  const nodes: ReactNode[] = [];
  let remaining = text;
  let i = 0;
  const push = (n: ReactNode) => nodes.push(<Fragment key={`${keyPrefix}-${i++}`}>{n}</Fragment>);

  const patterns: Array<{
    re: RegExp;
    render: (m: RegExpExecArray) => ReactNode;
  }> = [
    { re: /`([^`]+)`/, render: (m) => <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-[0.85em] font-mono">{m[1]}</code> },
    { re: /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/, render: (m) => <a href={m[2]} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:opacity-80">{m[1]}</a> },
    { re: /\*\*([^*]+)\*\*/, render: (m) => <strong className="font-extrabold text-foreground">{m[1]}</strong> },
    { re: /__([^_]+)__/, render: (m) => <strong className="font-extrabold text-foreground">{m[1]}</strong> },
    { re: /\*([^*\n]+)\*/, render: (m) => <em className="italic">{m[1]}</em> },
    { re: /_([^_\n]+)_/, render: (m) => <em className="italic">{m[1]}</em> },
  ];

  // Iteratively find the earliest match.
  while (remaining.length) {
    let best: { idx: number; len: number; node: ReactNode } | null = null;
    for (const { re, render } of patterns) {
      const m = re.exec(remaining);
      if (m && (best === null || m.index < best.idx)) {
        best = { idx: m.index, len: m[0].length, node: render(m) };
      }
    }
    if (!best) { push(remaining); break; }
    if (best.idx > 0) push(remaining.slice(0, best.idx));
    push(best.node);
    remaining = remaining.slice(best.idx + best.len);
  }
  return nodes;
}

export function Markdown({ source, className = "" }: { source: string; className?: string }) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Skip blank lines.
    if (!line.trim()) { i++; continue; }

    // Heading.
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const text = h[2].trim();
      const cls = level === 1
        ? "text-2xl sm:text-3xl font-extrabold text-foreground mt-6 mb-3"
        : level === 2
          ? "text-xl sm:text-2xl font-bold text-foreground mt-5 mb-2"
          : "text-lg font-bold text-foreground mt-4 mb-2";
      const Tag = (`h${level + 1}`) as "h2" | "h3" | "h4";
      blocks.push(<Tag key={key++} className={cls}>{renderInline(text, `h${key}`)}</Tag>);
      i++;
      continue;
    }

    // Blockquote.
    if (/^>\s?/.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quoted.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      blocks.push(
        <blockquote key={key++} className="border-s-4 border-primary/60 ps-4 py-1 my-3 text-muted-foreground italic">
          {renderInline(quoted.join(" "), `q${key}`)}
        </blockquote>,
      );
      continue;
    }

    // Unordered list.
    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={key++} className="list-disc ps-6 my-3 space-y-1.5 text-foreground/90">
          {items.map((it, idx) => <li key={idx}>{renderInline(it, `ul${key}-${idx}`)}</li>)}
        </ul>,
      );
      continue;
    }

    // Ordered list.
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push(
        <ol key={key++} className="list-decimal ps-6 my-3 space-y-1.5 text-foreground/90">
          {items.map((it, idx) => <li key={idx}>{renderInline(it, `ol${key}-${idx}`)}</li>)}
        </ol>,
      );
      continue;
    }

    // Paragraph (collect until blank line / block start).
    const para: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^#{1,3}\s+/.test(lines[i]) &&
      !/^[-*]\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !/^>\s?/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={key++} className="leading-relaxed text-foreground/90 my-3">
        {renderInline(para.join(" "), `p${key}`)}
      </p>,
    );
  }

  return <div className={className}>{blocks}</div>;
}
